import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Modal, Svg, useMediaQuery } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { variables } from '@rideiq/shared/components/variables';
import { createStore, GlobalState, StateMachineProvider, useStateMachine } from 'little-state-machine';

import { analyticsCreateRider, analyticsCreateRiderStep } from '../../../api/services/Analytics';
import { addRider, associateLocation } from '../../../api/services/Riders';
import { useAuthQuery } from '../../../auth/hooks/useAuthQuery';
import { useFlags } from '../../../contexts/LDContext';
import { useNavigation } from '../../../shared/hooks/useNavigation';
import { useToastStore } from '../../../shared/stores/toastStore';
import { calculateAge } from '../../../utils/helpers/calculateDate';
import { getRiderServices } from '../../api/services/RiderServices';
import { searchSisStudents } from '../../api/services/SisImport';

import type { SisStudentDetails } from './SisImportBanner/types';
import { CaregiverForm } from './CaregiverForm';
import * as formStateActions from './formStateActions';
import { ImportCaregiverForm, ImportCaregiverFormRef } from './ImportCaregiverForm';
import { PrimaryResidenceAddressForm } from './PrimaryResidenceAddressForm';
import {
  generateAnalyticsMustBeMetProtocols,
  newFormSubmitMap,
  removeSavedAttribute,
  useNewFormTitleMap,
} from './RideCreationModal.helpers';
import { ActionContainer, CloseButton } from './RiderCreationModal.styles';
import { RiderProfileForm } from './RiderProfileForm';
import { SafetySettingsForm } from './SafetySettingsForm';
import { SchoolAddressForm } from './SchoolAddressForm';
import type { SisStatus } from './SisImportBanner';

createStore({
  riderForm: {
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    gender: '',
    boosterSeat: false,
    frontSeatAuthorized: false,
    mustBeMet: false,
    residenceMustBeMet: false,
    residenceMustBeMetProtocol: {
      locationProtocol: '',
      authorizedContactGroups: [],
    },
    category: '',
    isAide: false,
    residence: {
      address: '',
      streetAddressTwo: '',
      name: '',
      pickupProcedureInstructions: '',
      dropoffProcedureInstructions: '',
      locationType: 'residence',
    },
    school: {
      address: '',
      streetAddressTwo: '',
      name: '',
      pickupProcedureInstructions: '',
      dropoffProcedureInstructions: '',
      locationType: 'school',
    },
    codeWord: '',
    riderNotes: '',
    passengersContactsAttributes: [{ firstName: '', lastName: '', role: '', relationship: '', phoneNumber: '' }],
    mobilePhoneNumber: '',
    externalId: '',
    photosAttributes: [{ attachment: new File([], '') }],
    email: '',
    isSharedBillingRider: false,
    sharedBillingRider: {
      residenceAccountContactName: '',
      residenceAccountContactEmail: '',
      residenceAccountId: null,
      residenceAccountContactId: null,
    },
    services: [],
    // Local flag only - NOT sent to API. Used to determine which Step 3 form to show.
    // True only when SIS import was used AND caregivers were found.
    usedSisImport: false,
  },
});

interface PresentationProps {
  handleClose: () => void;
  finalSubmit: (data: RiderFormType, state: GlobalState) => Promise<void>;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  defaultState: Partial<RiderFormType>;
  sisStatus: SisStatus | null;
}
interface RiderCreationModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSelectRider: (selectedRider) => void;
  riderForRide?: boolean;
  defaultState?: Partial<RiderFormType>;
}

export const RiderCreationModal: FC<RiderCreationModalProps> = ({
  isOpen,
  handleClose,
  riderForRide = false,
  defaultState = {},
  handleSelectRider,
}) => {
  const { navigateTo } = useNavigation();
  const { addToast } = useToastStore();
  const { userSettings } = useAuthQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enableSisImports } = useFlags();
  const sisEnabled = enableSisImports && userSettings?.sisIntegration === 'Yes';
  const [sisStatus, setSisStatus] = useState<SisStatus | null>(null);

  useEffect(() => {
    if (!sisEnabled) {
      return;
    }

    searchSisStudents('').then((response) => {
      if (response.type === 'success') {
        setSisStatus(response.data.students.length === 0 ? 'no-records' : 'available');
      }
    });
  }, [sisEnabled]);

  const finalSubmit = async (data, state) => {
    const { school, sharedBillingRider, isSharedBillingRider, usedSisImport, ...rest } = state.riderForm;

    // Filter to only selected caregivers and remove local-only fields before API submission
    // If 'selected' is undefined (non-SIS flow), include the caregiver
    const cleanedCaregivers = rest.passengersContactsAttributes
      .filter((c) => c.selected !== false)
      .map(({ isFromSis, selected, ...caregiver }) => caregiver);

    setIsSubmitting(true);
    const response = await addRider({
      ...rest,
      passengersContactsAttributes: cleanedCaregivers,
      ...(isSharedBillingRider
        ? {
            sharedBillingRider,
          }
        : {}),
    });

    // Clean state and remove the save attribtute for the residenceMustBeMetProtocol
    if (state.riderForm.residenceMustBeMetProtocol) {
      state.riderForm.residenceMustBeMetProtocol = removeSavedAttribute(state.riderForm.residenceMustBeMetProtocol);
    }

    // Clean state and remove the save attribtute for the schoolMustBeMetProtocol
    if (data.mustBeMetProtocol) {
      data.mustBeMetProtocol = removeSavedAttribute(data.mustBeMetProtocol);
    }

    const address = state.riderForm.residence.address;
    const addressID = state.riderForm.residence.address?.id;
    const residence = state.riderForm.residence;
    const mustBeMet = state.riderForm.residenceMustBeMet;
    const mustBeMetProtocol = state.riderForm.residenceMustBeMetProtocol;

    const riderMustBeMetProtocols = generateAnalyticsMustBeMetProtocols(
      { mustBeMet, mustBeMetProtocol, residence },
      { mustBeMet: data.mustBeMet, mustBeMetProtocol: data.mustBeMetProtocol, school: data.school }
    );

    if (response?.type === 'success') {
      // New way: location is already created/updated by the form, just associate it
      school?.id && (await updateAndAssociateLocation(response.data.slug, school.id));
      address &&
        addressID &&
        (await updateAndAssociateLocation(response.data.slug, addressID, mustBeMet, mustBeMetProtocol));
      data.school?.address?.id &&
        (await updateAndAssociateLocation(
          response.data.slug,
          data.school.address.id,
          data.mustBeMet,
          data.mustBeMetProtocol
        ));

      analyticsCreateRider({
        riderSlug: response.data.slug,
        category: state.riderForm.category,
        hasPhoto: !!state.riderForm.photosAttributes[0]?.attachment?.name,
        hasPhone: !!state.riderForm.mobilePhoneNumber,
        numCareGivers: state.riderForm.passengersContactsAttributes.filter((c) => c.role === 'caregiver').length,
        numEContacts: state.riderForm.passengersContactsAttributes.filter((c) => c.role === 'emergency_contact').length,
        hasEmail: !!state.riderForm.email,
        isShared: state.riderForm.isSharedBillingRider,
        firstName: state.riderForm.firstName,
        lastName: state.riderForm.lastName,
        ...(riderMustBeMetProtocols.length > 0 && { riderMustBeMetProtocols }),
        ...(state.riderForm.services.length > 0 && { hasService: state.riderForm.services }),
      });
      riderForRide ? handleRiderForRide(state, response.data.slug) : navigateTo(`/riders/${response.data.slug}`);
    } else {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    }
    setIsSubmitting(false);
  };

  const handleRiderForRide = (state, slug) => {
    const { dateOfBirth, boosterSeat, firstName, lastName, riderNotes, photosAttributes } = state.riderForm;

    const selectedRider = {
      age: calculateAge(dateOfBirth),
      booster: boosterSeat,
      id: slug,
      label: `${firstName} ${lastName}`,
      name: `${firstName} ${lastName}`,
      notes: riderNotes,
      photo: photosAttributes[0].attachment.name ? URL.createObjectURL(photosAttributes[0].attachment) : '',
    };

    handleSelectRider(selectedRider);
    handleClose();
  };

  const updateAndAssociateLocation = async (riderSlug, locationId, mustBeMet?, mustBeMetProtocol?) => {
    const response = await associateLocation(riderSlug, locationId, {
      primary: true,
      ...(mustBeMet && {
        mustBeMet: true,
        locationProtocol: mustBeMetProtocol.locationProtocol,
        authorizedContactGroups: mustBeMetProtocol.authorizedContactGroups,
      }),
    });

    if (response?.type === 'error') {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    }
  };

  return isOpen ? (
    <StateMachineProvider>
      <Presentation
        handleClose={handleClose}
        finalSubmit={finalSubmit}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        defaultState={defaultState}
        sisStatus={sisStatus}
      />
    </StateMachineProvider>
  ) : null;
};

const Presentation: FC<PresentationProps> = ({
  finalSubmit,
  handleClose,
  isSubmitting,
  setIsSubmitting,
  defaultState,
  sisStatus,
}) => {
  const isMobileOrTablet = useMediaQuery({ maxWidth: variables.breakpoints.desktop - 1 });
  const [formStep, setFormStep] = useState(1);
  const { actions, state } = useStateMachine(formStateActions);
  const finishButtonText = isSubmitting ? 'Saving...' : 'Finish';
  const [isInitialized, setIsInitialized] = useState(false);
  const prev = useRef<Date | null>(null);
  const importCaregiverFormRef = useRef<ImportCaregiverFormRef>(null);
  const [services, setServices] = useState<RiderService[]>([]);

  useEffect(() => {
    const fetchServices = async () => {
      const response = await getRiderServices();

      if (response?.type === 'success') {
        setServices(response.data.services);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const prevDate = prev.current;

    const preTime = prevDate instanceof Date ? prevDate.getTime() : null;
    const newTime = state.riderForm?.dateOfBirth instanceof Date ? state.riderForm?.dateOfBirth.getTime() : null;

    if (preTime !== newTime || isInitialized) {
      setIsInitialized(false);
    }
    prev.current = state.riderForm?.dateOfBirth;
  }, [state.riderForm?.dateOfBirth]);

  useLayoutEffect(() => {
    actions.clearAction();
    actions.updateFormStateAction(defaultState);
  }, []);

  useEffect(() => {
    analyticsCreateRiderStep(formStep);
  }, [formStep]);

  const riderProfileSubmit = (data) => {
    actions.checkSafetySettings({ ...data, services });
    actions.updateFormStateAction(data);
    setFormStep(2);
  };

  const safetySettingsSubmit = (data) => {
    actions.updateFormStateAction(data);
    setFormStep(3);
  };
  const caregiverSubmit = (data) => {
    actions.updatePassengersContactsAttributesAction(data);
    setFormStep(4);
  };

  // Handler for SIS import - transforms and stores caregiver data, sets usedSisImport flag
  const handleSisImport = (sisData: SisStudentDetails) => {
    const hasCaregivers = sisData.caregivers && sisData.caregivers.length > 0;

    if (hasCaregivers) {
      // Transform SIS caregivers to our format and populate passengersContactsAttributes
      // Note: sisData is camelCase (axios transformed from Rails API snake_case)
      const transformedCaregivers = sisData.caregivers.map((caregiver) => ({
        firstName: caregiver.firstName,
        lastName: caregiver.lastName,
        role: 'caregiver',
        relationship: '',
        phoneNumber: caregiver.phone || '',
        email: caregiver.email || '',
        isFromSis: true, // Flag to identify SIS-sourced caregivers (local only, not sent to API)
        selected: false, // Not selected by default
      }));

      actions.updatePassengersContactsAttributesAction(transformedCaregivers);
      actions.updateFormStateAction({ usedSisImport: true });
    }
    // If no caregivers found, usedSisImport stays false and regular CaregiverForm will be shown
  };

  // Show ImportCaregiverForm only when SIS import was used AND caregivers were found
  const showImportCaregiverForm = !!sisStatus && state.riderForm?.usedSisImport;

  const primaryResidenceAddressSubmit = (data) => {
    const modifiedData = {
      residence: data.residence,
      residenceMustBeMet: data.mustBeMet,
      residenceMustBeMetProtocol: data.mustBeMetProtocol,
    };

    actions.updatePassengersResidenceAttributesAction(modifiedData);
    setFormStep(5);
  };

  const newFormTitleMap = useNewFormTitleMap();

  const getModalTitle = () => newFormTitleMap.get(formStep.toString());

  const getModalStepButton = () => (formStep === 5 ? finishButtonText : 'Next');

  const getFormId = () => {
    if (formStep === 3 && showImportCaregiverForm) {
      return 'import-caregiver-form';
    }

    return newFormSubmitMap.get(formStep.toString());
  };

  return (
    <Modal open={true} maxWidth={isMobileOrTablet ? 'xs' : 'xl'}>
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="8px">
          <Box component="span" sx={{ fontSize: '18px' }}>
            {getModalTitle()}
          </Box>
          <CloseButton
            onClick={() => {
              handleClose();
              actions.clearAction();
            }}
            data-testid="closeButton"
            aria-label="Close"
            type="button"
          >
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content sx={{ padding: { xs: '8px 16px 24px', sm: '8px 30px 24px' } }}>
        <>
          {formStep === 1 && state.riderForm && (
            <RiderProfileForm
              riderDetails={{
                firstName: state.riderForm?.firstName,
                lastName: state.riderForm?.lastName,
                dateOfBirth: state.riderForm?.dateOfBirth,
                mobilePhoneNumber: state.riderForm?.mobilePhoneNumber,
                gender: state.riderForm?.gender,
                category: state.riderForm?.category,
                externalId: state.riderForm?.externalId,
                photosAttributes: state.riderForm?.photosAttributes,
                email: state.riderForm?.email,
                isSharedBillingRider: state.riderForm.isSharedBillingRider,
                sharedBillingRider: state.riderForm.sharedBillingRider,
                isAide: state.riderForm.isAide,
              }}
              services={services}
              setIsSubmitting={setIsSubmitting}
              onSubmit={riderProfileSubmit}
              onSisImport={handleSisImport}
              sisStatus={sisStatus ?? undefined}
            />
          )}
          {formStep === 2 && (
            <SafetySettingsForm
              isInitialized={isInitialized}
              setIsInitialized={setIsInitialized}
              safetySettings={{
                codeWord: state.riderForm.codeWord,
                boosterSeat: state.riderForm.boosterSeat,
                frontSeatAuthorized: state.riderForm.frontSeatAuthorized,
                mustBeMet: state.riderForm.mustBeMet,
                riderNotes: state.riderForm.riderNotes,
                services: state.riderForm.services,
              }}
              onSubmit={safetySettingsSubmit}
              setIsSubmitting={setIsSubmitting}
              onCancel={() => setFormStep(3)}
              dateOfBirth={state.riderForm.dateOfBirth}
            />
          )}
          {formStep === 3 && !showImportCaregiverForm && (
            <CaregiverForm
              contact={state.riderForm.passengersContactsAttributes[0]}
              onSubmit={caregiverSubmit}
              onCancel={() => false}
              setIsSubmitting={setIsSubmitting}
            />
          )}
          {formStep === 3 && showImportCaregiverForm && (
            <ImportCaregiverForm
              ref={importCaregiverFormRef}
              initialCaregivers={state.riderForm.passengersContactsAttributes}
              onSubmit={(caregivers) => {
                actions.updatePassengersContactsAttributesAction(caregivers);
                setFormStep(4);
              }}
              setIsSubmitting={setIsSubmitting}
            />
          )}
          {formStep === 4 && (
            <PrimaryResidenceAddressForm
              addressDetails={{
                residence: state.riderForm.residence,
                mustBeMet: state.riderForm.residenceMustBeMet,
                mustBeMetProtocol: state.riderForm.residenceMustBeMetProtocol,
              }}
              contacts={[
                {
                  firstName: state.riderForm?.passengersContactsAttributes[0]?.firstName,
                  lastName: state.riderForm?.passengersContactsAttributes[0]?.lastName,
                  mobilePhoneNumber: state.riderForm?.passengersContactsAttributes[0]?.phoneNumber,
                },
              ]}
              onSubmit={(data) => primaryResidenceAddressSubmit(data)}
            />
          )}
          {formStep === 5 && (
            <SchoolAddressForm category={state.riderForm?.category} onSubmit={(data) => finalSubmit(data, state)} />
          )}
        </>
      </Modal.Content>
      <Modal.Actions>
        <ActionContainer>
          {formStep !== 1 && (
            <Button
              className="back-button"
              color="tertiary"
              onClick={() => setFormStep(formStep - 1)}
              loading={isSubmitting}
            >
              Back
            </Button>
          )}
          <Box display="flex" gap={1} marginLeft="auto">
            {formStep === 3 && showImportCaregiverForm && (
              <Button color="secondary" onClick={() => importCaregiverFormRef.current?.addManually()}>
                Add manually
              </Button>
            )}
            <Button className="submit-button" color="primary" type="submit" form={getFormId()} loading={isSubmitting}>
              {formStep === 3 && showImportCaregiverForm ? 'Import' : getModalStepButton()}
            </Button>
          </Box>
        </ActionContainer>
      </Modal.Actions>
    </Modal>
  );
};
